import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Form, Alert, Spinner, Card, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFileAlt, faXmark, faDollarSign, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import api, { getOrders, getOrderDetails } from '../services/api';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { API_URL } from '../services/api';
import { safeBlob } from '../utils/documents';
import DocumentViewerModal from './DocumentViewerModal';
import '../styles/modal-shared.css';

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState({
    items: [],
    rates: [],
    documents: []
  });

  // Document viewer modal state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getOrders(dateRange.startDate, dateRange.endDate);
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [dateRange]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyDateFilter = () => {
    if (dateRange.startDate <= dateRange.endDate) {
      fetchOrders();
    } else {
      toast.error("Start date must be before end date");
    }
  };

  const handleOrderClick = async (order) => {
    try {
      setSelectedOrder(order);
      const details = await getOrderDetails(order.idTrans);
      setOrderDetails(details);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Failed to load order details:", error);
      toast.error(`Failed to load order details: ${error.message || 'Unknown error'}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('MM/DD/YY');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const grandTotal = useMemo(() => {
    if (!orderDetails?.rates || orderDetails.rates.length === 0) return 0;
    return orderDetails.rates.reduce((sum, rate) => {
      return sum + (parseFloat(rate.qty) || 0) * (parseFloat(rate.rate) || 0);
    }, 0);
  }, [orderDetails?.rates]);

  const handlePrintReceipt = async () => {
    if (!selectedOrder) return;
    try {
      toast.loading("Generating receipt...");
      const response = await api.post(`/api/orders/${selectedOrder.idTrans}/receipt`, {
        includeDetails: true
      }, { responseType: 'blob' });
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      const receiptDocument = {
        descr: `Receipt for Order ${selectedOrder.idTrans}`,
        fileName: `Receipt_${selectedOrder.idTrans}.pdf`,
        blobUrl,
        type: contentType || 'application/pdf'
      };
      toast.dismiss();
      toast.success("Receipt generated successfully");
      setSelectedDocument(receiptDocument);
      setShowDocumentModal(true);
    } catch (error) {
      console.error("Failed to generate receipt:", error);
      toast.dismiss();
      toast.error(`Failed to generate receipt: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDocumentClick = async (doc, e) => {
    e.preventDefault();
    if (doc.isReceipt) {
      handlePrintReceipt();
      return;
    }
    toast.loading('Loading document…');
    try {
      // Backend streams the blob — fetch as blob, create a local object URL
      const res = await api.get(`/api/orders/documents/${encodeURIComponent(doc.fileName)}`, { responseType: 'blob' });
      // Only a render-safe type survives into the object URL — see utils/documents.
      const blob = safeBlob(res.data, res.headers['content-type'] || 'application/pdf');
      const blobUrl = URL.createObjectURL(blob);
      toast.dismiss();
      setSelectedDocument({ ...doc, blobUrl, url: null });
      setShowDocumentModal(true);
    } catch (err) {
      toast.dismiss();
      toast.error(`Failed to load document: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const loadingToast = toast.loading(`Uploading ${files.length} document(s)...`);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('descr', file.name);
        const response = await api.post(`/api/orders/${selectedOrder.idTrans}/documents`, formData);
        setOrderDetails(prev => ({
          ...prev,
          documents: [...prev.documents, response.data]
        }));
      }
      toast.success(`Successfully uploaded ${files.length} document(s)`);
    } catch (error) {
      toast.error(`Failed to upload document: ${error.message || 'Unknown error'}`);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Container fluid className="px-2">
      <Card className="shadow-sm">
        <Card.Header className="py-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <h5 className="m-0 font-weight-bold text-primary">
              Transactions
              <small className="ms-2 text-muted" style={{ fontSize: '0.7rem' }}>Click on a transaction for more info</small>
            </h5>
            <div className="d-flex gap-3 flex-wrap mt-2 mt-sm-0">
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">From:</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                  size="sm"
                />
              </div>
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">To:</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                  size="sm"
                />
              </div>
              <Button variant="primary" size="sm" onClick={handleApplyDateFilter}>
                <FontAwesomeIcon icon={faSearch} className="me-1" />
                Search
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {orders.length === 0 ? (
            <Alert variant="info" className="m-3">No orders found for the selected date range.</Alert>
          ) : (
            <div style={{ height: 'calc(100vh - 200px)', minHeight: '400px', overflowY: 'auto' }}>
              <Table hover size="sm" className="mb-0">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>Order #</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Reference</th>
                    <th>Ship To</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.idTrans}
                      onClick={() => handleOrderClick(order)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{order.idTrans}</td>
                      <td>{formatDate(order.transDate)}</td>
                      <td>
                        {order.approvalStatus === 'pending' ? (
                          <span className="badge bg-warning text-dark">Pending Approval</span>
                        ) : order.approvalStatus === 'rejected' ? (
                          <span className="badge bg-danger">Rejected</span>
                        ) : (
                          order.typeName
                        )}
                      </td>
                      <td>{order.CustomerRef || '-'}</td>
                      <td>{order.delName || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Order Detail Modal ── */}
      <Modal
        size="xl"
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        centered
        dialogClassName="detail-modal"
      >
        <Modal.Header closeButton>
          <div className="modal-title-row">
            <div className="modal-header-icon">
              <FontAwesomeIcon icon={faClipboardList} />
            </div>
            <div className="modal-header-text">
              <h5>Order #{selectedOrder?.idTrans}</h5>
              {selectedOrder?.typeName && (
                <p className="modal-subtitle">{selectedOrder.typeName}</p>
              )}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Order + Shipping Info Grid */}
          <div className="detail-info-grid">
            <div className="detail-info-field">
              <div className="detail-info-label">Order #</div>
              <div className="detail-info-value">{selectedOrder?.idTrans}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Date</div>
              <div className="detail-info-value">{formatDate(selectedOrder?.transDate) || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Type</div>
              <div className="detail-info-value">{selectedOrder?.typeName || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Reference</div>
              <div className="detail-info-value">{selectedOrder?.CustomerRef || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            {selectedOrder?.approvalStatus && (
              <div className="detail-info-field">
                <div className="detail-info-label">Status</div>
                <div className="detail-info-value">
                  {selectedOrder.approvalStatus === 'pending' && (
                    <span className="detail-status detail-status--pending">Pending Approval</span>
                  )}
                  {selectedOrder.approvalStatus === 'approved' && (
                    <span className="detail-status detail-status--complete">Approved</span>
                  )}
                  {selectedOrder.approvalStatus === 'rejected' && (
                    <span className="detail-status detail-status--rejected">Rejected</span>
                  )}
                </div>
              </div>
            )}
            <div className="detail-info-field">
              <div className="detail-info-label">Ship To</div>
              <div className="detail-info-value">{orderDetails.delName || <span className="detail-info-value--muted">—</span>}</div>
              {orderDetails.delAddress && (
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                  {orderDetails.delAddress}<br />
                  {[orderDetails.delCity, orderDetails.delState, orderDetails.delZip].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Phone</div>
              <div className="detail-info-value">{orderDetails.delPhone || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            {orderDetails.delNotes && (
              <div className="detail-notes-field">
                <div className="detail-info-label">Notes</div>
                <div className="detail-notes-value">{orderDetails.delNotes}</div>
              </div>
            )}
          </div>

          {/* Items */}
          <div className="detail-section">
            <div className="detail-section-header">
              <span className="detail-section-title">Order Items</span>
              {orderDetails.items?.length > 0 && (
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {orderDetails.items.length} item{orderDetails.items.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className={`detail-section-body${(orderDetails.items?.length || 0) > 5 ? ' detail-section-body--scroll' : ''}`}>
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>Lot ID</th>
                    <th>Item Number</th>
                    <th>Description</th>
                    <th>Notes</th>
                    <th>PPU</th>
                    <th>Quantity</th>
                    <th>Total Pieces</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.items?.length > 0 ? orderDetails.items.map((item, index) => (
                    <tr key={item.idTrans2 || index}>
                      <td>{item.recTrans || '—'}</td>
                      <td>{item.itemNumber || '—'}</td>
                      <td>{item.itemName || '—'}</td>
                      <td>{item.notes || '—'}</td>
                      <td>{Number(item.ppu || 1).toLocaleString()}</td>
                      <td>{Number(item.transQty || 0).toLocaleString()}</td>
                      <td>{Number((item.transQty || 0) * (item.ppu || 1)).toLocaleString()}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" className="detail-table-empty">No items found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rates + Documents */}
          <div className="detail-two-col">
            {/* Rates */}
            <div className="detail-section" style={{ margin: 0 }}>
              <div className="detail-section-header">
                <span className="detail-section-title">
                  <FontAwesomeIcon icon={faDollarSign} style={{ marginRight: 5 }} />
                  Rates
                </span>
                {grandTotal > 0 && (
                  <span className="detail-section-total">{formatCurrency(grandTotal)}</span>
                )}
              </div>
              <div className={`detail-section-body${(orderDetails.rates?.length || 0) > 5 ? ' detail-section-body--scroll' : ''}`}>
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetails.rates?.length > 0 ? orderDetails.rates.map((rate, index) => (
                      <tr key={rate.idInvRating || index}>
                        <td>{rate.descr || '—'}</td>
                        <td>{rate.qty || 0}</td>
                        <td>{formatCurrency(rate.rate)}</td>
                        <td>{formatCurrency((rate.qty || 0) * (rate.rate || 0))}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="detail-table-empty">No rates found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Documents */}
            <div className="detail-section" style={{ margin: 0 }}>
              <div className="detail-section-header">
                <span className="detail-section-title">
                  <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 5 }} />
                  Documents
                </span>
              </div>
              <div className="detail-section-body">
                <ul className="detail-doc-list">
                  <li className="detail-doc-item" onClick={(e) => handleDocumentClick({ isReceipt: true }, e)}>
                    <FontAwesomeIcon icon={faFileAlt} className="doc-icon" />
                    Receipt for Order {selectedOrder?.idTrans}
                  </li>
                  {orderDetails.documents?.map((doc, index) => (
                    <li key={doc.idDocument || index} className="detail-doc-item" onClick={(e) => handleDocumentClick(doc, e)}>
                      <FontAwesomeIcon icon={faFileAlt} className="doc-icon" />
                      {doc.descr || doc.fileName || 'Document'}
                    </li>
                  ))}
                  {orderDetails.documents?.length === 0 && (
                    <li className="detail-doc-item" style={{ color: '#94a3b8', cursor: 'default', fontStyle: 'italic' }}>
                      No additional documents
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button className="detail-btn detail-btn--secondary" onClick={() => setShowDetailModal(false)}>
            <FontAwesomeIcon icon={faXmark} />
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Document Viewer ── */}
      <DocumentViewerModal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        document={selectedDocument}
      />
    </Container>
  );
}

export default Orders;
